import { useEffect, useState, useMemo } from 'react';
import { GridToolbar } from '@mui/x-data-grid';
import { NoRowsGrid, NoResultsGrid } from '../mui-components/NoRowsGrid';
import { Typography, Stack, Link } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { addIds } from '../utils/helpers';
import Loading from '../mui-components/Loading';
import { useGlobalContext } from '../context/GlobalStore';
import apiClient from '../utils/apiClient';
import moment from 'moment';

const staticColumns = [
  { field: 'Id', headerName: 'Measurement Id', width: 130 },
  { field: 'MeasurementType', headerName: 'Measurement Type', width: 160 },
  {
    field: 'MeasuredAt',
    headerName: 'Measured At',
    width: 200,
    valueGetter: (value) => {
      if (!value) {
        return value;
      }
      return new Date(value);
    },
    valueFormatter: (value) => {
      return value ? moment(value).format('L, LT') : value;
    },
  },
  { field: 'DeviceId', headerName: 'Device Id', width: 100 },
  { field: 'DeviceModel', headerName: 'Device Model', width: 130 },
  { field: 'DeviceType', headerName: 'Device Type', width: 130 },
  { field: 'DeviceSim', headerName: 'Device Sim', width: 130 },
  {
    field: 'SignalStrengthPercentage',
    headerName: 'Signal Strength',
    width: 180,
  },
  {
    field: 'RadioAccessTechnology',
    headerName: 'Radio Access Technology',
    width: 200,
  },
  { field: 'NetworkName', headerName: 'Network Name', width: 120 },
  {
    field: 'TowerLocation',
    headerName: 'Tower Latitude, Longitude',
    width: 200,
    sortable: false,
    renderCell: (params) => {
      const lat = params.row.TowerLatitude;
      const long = params.row.TowerLongitude;

      if (lat && long) {
        return (
          <Link
            href={`https://www.google.com/maps/search/?api=1&query=${lat},${long}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {`${lat}, ${long}`}
          </Link>
        );
      }
      return null;
    },
  },
];

export default function Readings({ token, id, accountId, currentApiKey }) {
  const [readings, setReadings] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsloading] = useState(true);
  const { state } = useGlobalContext();

  useEffect(() => {
    if (currentApiKey) {
      setIsloading(true);
      getReadings();
    }
  }, [currentApiKey]);

  async function getReadings() {
    try {
      const apiKeyObj = state.account?.pylo_accounts?.find(
        (account) => account.name === currentApiKey
      );
      const apiKeyId = apiKeyObj?.id || accountId;

      const response = await apiClient.post(
        '/readings',
        {
          imei: id,
          account_id: apiKeyId,
        },
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );

      if (response.length > 0) {
        // Add id property for DataGrid
        const rows = addIds(response).map(row => ({
          ...row,
          id: row.Id,
        }));
        setReadings(rows);

        // Build dynamic columns from Values
        const allTypes = Array.from(new Set(
          response.flatMap(reading => (reading.Values || []).map(v => v.Type))
        ));
        const dynamicColumns = allTypes.map(type => ({
          field: type,
          headerName: type.charAt(0).toUpperCase() + type.slice(1),
          width: 120,
          // No valueGetter needed, see below
        }));
        setColumns([...staticColumns, ...dynamicColumns]);
      } else {
        setReadings([]);
        setColumns([...staticColumns]);
      }
    } catch (e) {
      console.log('POST call failed: ', JSON.parse(e.response.body));
      setReadings([]);
      setColumns([...staticColumns]);
    } finally {
      setIsloading(false);
    }
  }

  // Helper to flatten Values into row fields for DataGrid
  const processedRows = useMemo(() => {
    return readings.map(row => {
      const valuesObj = (row.Values || []).reduce((acc, v) => {
        acc[v.Type] = v.Value;
        return acc;
      }, {});
      return { ...row, ...valuesObj };
    });
  }, [readings]);

  return (
    <Stack sx={{ height: 'calc(100vh - 400px)' }} mt={2}>
      <Typography variant="h5" mb={1} color="grey">
        Readings
      </Typography>
      {isLoading || columns.length === 0 ? (
        <Loading />
      ) : (
        <DataGridPro
          rows={processedRows}
          columns={columns}
          getRowId={row => row.id}
          slots={{
            toolbar: GridToolbar,
            noRowsOverlay: NoRowsGrid,
            noResultsOverlay: NoResultsGrid,
          }}
          pagination={true}
          sx={{
            '& .MuiDataGridPro-overlay': { minHeight: 300 },
            '& .MuiDataGrid-overlayWrapper': { minHeight: 300 },
          }}
        />
      )}
    </Stack>
  );
}